'use client';

import { useRef } from 'react';

import { useTranslations } from 'use-intl';

import { MAX_RESPONSE_LENGTH } from '@/shared-question-model';
import { useProjectResponseModeContext } from '@/shared-survey';
import {
  SharedUiComposer,
  WAVEFORM_PLAYER_CUSTOM_STOP_EVENT,
} from '@/shared-ui';

import { wrapOnChangeWithWaveformStop } from './project-response-open-end-send.helpers';
import { ProjectResponseSendComponentProps } from './project-response-renderer.type';
import { useProjectResponseSend } from './project-response-send.hooks';

export default function ProjectResponseOpenEndSendComponentMobile({
  register,
  onKeyDown,
  isSubmitting,
  value,
  disabled,
  isConversationOver,
  type,
  isUserVoiceEnabled,
}: ProjectResponseSendComponentProps) {
  const t = useTranslations('Chat');

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const { handleToggle } = useProjectResponseSend(type);
  const { responseMode } = useProjectResponseModeContext();

  const onToggleVoice = () => {
    const wasVoice = responseMode === 'voice';
    handleToggle();
    if (wasVoice && !isConversationOver) {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  };

  const reg = register?.('content');

  const setTextareaRef = (el: HTMLTextAreaElement | null) => {
    reg?.ref(el);
    textareaRef.current = el;
  };

  const regProps = reg
    ? {
        name: reg.name,
        onChange: wrapOnChangeWithWaveformStop(reg.onChange, () =>
          window.dispatchEvent(new Event(WAVEFORM_PLAYER_CUSTOM_STOP_EVENT))
        ),
        onBlur: reg.onBlur,
      }
    : undefined;

  return (
    /* oxlint-disable jsx-a11y/no-autofocus */
    <SharedUiComposer
      size='compact'
      type='write'
      data-test='open-end-send-mobile'
      submitType='submit'
      placeholder={t('inputPlaceholder')}
      maxLength={MAX_RESPONSE_LENGTH}
      autoFocus
      textareaAriaLabel={t('textareaAriaLabel')}
      textareaRef={setTextareaRef}
      textareaDisabled={isConversationOver}
      textareaProps={regProps}
      onKeyDown={isSubmitting ? undefined : onKeyDown}
      canSubmit={Boolean(value?.trim())}
      loading={isSubmitting}
      disabled={disabled}
      submitAriaLabel={t('send')}
      submitClassName='bg-(--brand-primary) text-(--brand-primary-contrast) [&_svg]:text-(--brand-primary-contrast) enabled:hover:bg-(--brand-primary) disabled:bg-(--brand-primary) disabled:opacity-20'
      isVoiceEnabled={isUserVoiceEnabled}
      onToggleVoice={onToggleVoice}
      voiceToggleDisabled={isConversationOver}
      voiceToggleAriaLabel={t('voiceToggleAriaLabel')}
    />
    /* oxlint-enable jsx-a11y/no-autofocus */
  );
}
